import React, {useEffect, useRef, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {Col, Row} from "react-bootstrap";
import ReactPlayer from "react-player";
import {Empty, message, Spin} from "antd";
import CourseVideoCard from "../../../../../components/CourseVideoCard";
import "./CourseVideoDetails.scss";
import {GoChevronLeft, GoChevronRight} from "react-icons/go";
import {BackIcon, CoursesVideoIconBig} from "../../../../../assets/icons/defaultIcons";
import {CourseService} from "../../../../services/course-api.service";
import dayjs from 'dayjs';

// Add custom CSS to prevent video downloads (similar to VaultDetails)
const preventDownloadStyles = `
    .player video::-internal-media-controls-download-button {
        display: none !important;
    }
    
    .player video::-webkit-media-controls-enclosure {
        overflow: hidden !important;
    }
    
    .player video::-webkit-media-controls-panel {
        width: calc(100% + 30px) !important;
    }
    
    /* Disable text selection on the player */
    .player {
        user-select: none;
        -webkit-user-select: none;
        -moz-user-select: none;
        -ms-user-select: none;
    }
`;

const CourseVideoDetails = () => {
    const [playing, setPlaying] = useState(false);
    const [videoData, setVideoData] = useState(null);
    const [courseData, setCourseData] = useState(null);
    const [courseVideos, setCourseVideos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [upNext, setUpNext] = useState(null);
    const [videoEnded, setVideoEnded] = useState(false);
    const [nextVideoCountdown, setNextVideoCountdown] = useState(5);
    const [loadingNextVideo, setLoadingNextVideo] = useState(false);
    const [error, setError] = useState(null);
    const [hasSeenToResumePosition, setHasSeenToResumePosition] = useState(false);
    const playerRef = useRef(null);
    const watchedTimeRef = useRef(0);
    const countdownTimerRef = useRef(null);
    const { courseId, videoId } = useParams();
    const navigate = useNavigate();

    // Format time for display (MM:SS)
    const formatTime = (seconds) => {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = Math.floor(seconds % 60);
        return `${String(minutes).padStart(2, "0")}:${String(remainingSeconds).padStart(2, "0")}`;
    };

    // Convert time string (MM:SS) to seconds
    const timeToSeconds = (timeString) => {
        if (!timeString) return 0;
        const parts = timeString.split(':').map(Number);
        if (parts.length === 2) {
            return parts[0] * 60 + parts[1]; // MM:SS format
        } else if (parts.length === 3) {
            return parts[0] * 3600 + parts[1] * 60 + parts[2]; // HH:MM:SS format
        }
        return 0;
    };

    // Send video progress to API
    const sendWatchTime = async () => {
        const watchedAtFormatted = formatTime(watchedTimeRef.current);

        if (watchedTimeRef.current > 0 && videoId) {
            try {
                console.log("Sending watch time:", {
                    vaultId: videoId,
                    watchedAt: watchedAtFormatted,
                });

                await CourseService.trackCourseVideoProgress({
                    vaultId: videoId,
                    watchedAt: watchedAtFormatted,
                    // courseId: courseId
                });
            } catch (error) {
                console.error("Error sending watch time:", error);
            }
        }
    };

    // Track video progress
    const handleProgress = (progress) => {
        watchedTimeRef.current = progress.playedSeconds;

        // Optional: Log progress for debugging (remove in production)
        // console.log("Progress:", formatTime(progress.playedSeconds), "of", videoData?.duration);
    };

    // Handle pause event
    const handlePause = () => {
        sendWatchTime();
    };

    // Handle when player is ready - seek to watched position
    const handlePlayerReady = () => {
        console.log("Player ready, checking for resume position...");

        if (videoData?.current && playerRef.current && !hasSeenToResumePosition) {
            const resumeTimeInSeconds = timeToSeconds(videoData.current);
            console.log("Resume position found:", videoData.current, "->", resumeTimeInSeconds, "seconds");

            // Only seek if there's meaningful watch progress (more than 10 seconds)
            if (resumeTimeInSeconds > 10) {
                console.log("Seeking to position:", resumeTimeInSeconds);
                playerRef.current.seekTo(resumeTimeInSeconds, 'seconds');
                setHasSeenToResumePosition(true);

                // Show a brief notification that video was resumed
                message.info(`Resumed video from ${videoData.current}`, 3);
            }
        }

        setPlaying(true);
    };

    // Navigate to previous video
    const handlePreviousVideo = () => {
        // Stop countdown if it's running
        clearNextVideoTimer();

        // Find the current video index in the array
        const currentIndex = courseVideos.findIndex(video => video.id === videoId);

        if (currentIndex > 0) {
            // Go to the previous video
            setLoadingNextVideo(true);
            navigate(`/courses/${courseId}/videos/${courseVideos[currentIndex - 1].id}`);
        }
    };

    // Navigate to next video
    const handleNextVideo = () => {
        // Stop countdown if it's running
        clearNextVideoTimer();

        // Find the current video index in the array
        const currentIndex = courseVideos.findIndex(video => video.id === videoId);

        if (currentIndex >= 0 && currentIndex < courseVideos.length - 1) {
            // Go to the next video
            setLoadingNextVideo(true);
            navigate(`/courses/${courseId}/videos/${courseVideos[currentIndex + 1].id}`);
        }
    };

    // Clear the next video countdown timer
    const clearNextVideoTimer = () => {
        if (countdownTimerRef.current) {
            clearInterval(countdownTimerRef.current);
            countdownTimerRef.current = null;
        }
    };

    // Handle video end event
    const handleVideoEnded = () => {
        setVideoEnded(true);
        sendWatchTime();

        // Find the current video index in the array
        const currentIndex = courseVideos.findIndex(video => video.id === videoId);
        const hasNextVideo = currentIndex >= 0 && currentIndex < courseVideos.length - 1;

        if (hasNextVideo) {
            // Set up next video
            setNextVideoCountdown(5);

            countdownTimerRef.current = setInterval(() => {
                setNextVideoCountdown(prev => {
                    if (prev <= 1) {
                        clearNextVideoTimer();
                        handleNextVideo();
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        }
    };

    // Cancel autoplay next video
    const cancelAutoplay = () => {
        clearNextVideoTimer();
        setVideoEnded(false);
    };

    // Load course data
    useEffect(() => {
        const fetchCourseData = async () => {
            if (!courseId) return;

            try {
                setLoading(true);
                setError(null);

                const response = await CourseService.getCourseById(courseId);
                console.log(response,'courses details video')
                if (!response.data?.data?.data) {
                    throw new Error("Course data not found");
                }

                const course = response.data.data.data;

                // Format course data
                setCourseData({
                    id: course._id,
                    title: course.title,
                    description: course.description,
                    thumbnail: course.thumbnail,
                    author: course.userDetails?.fullName || "Unknown Author",
                    experience: course.userDetails?.additionalFields?.businessIndustry || "Educator"
                });

                // Format video data if available
                if (course.videoDetails && Array.isArray(course.videoDetails)) {
                    const formattedVideos = course.videoDetails.map((video, index) => ({
                        id: video._id,
                        number: index + 1, // Use 1-based indexing for display
                        displayNumber: index + 1, // For showing to users
                        thumbnail: video.thumbnail || course.thumbnail,
                        title: video.name,
                        description: video.description || course.description,
                        url: video.url,
                        duration: video.length || "00:00",
                        current: video.vaultWatchHistory?.watchedAt,
                        date: new Date(video.createdAt).toLocaleDateString()
                    }));

                    setCourseVideos(formattedVideos);
                }

            } catch (err) {
                console.error("Error fetching course data:", err);
                setError("Failed to load course details");
                message.error("Failed to load course data");
            } finally {
                setLoading(false);
            }
        };

        fetchCourseData();

        // Cleanup function
        return () => {
            sendWatchTime();
            clearNextVideoTimer();
        };
    }, [courseId]);

    // Load video data
    useEffect(() => {
        if (!courseVideos.length || !videoId) return;

        setLoading(true);
        setVideoEnded(false);
        setHasSeenToResumePosition(false); // Reset seek state for new video
        clearNextVideoTimer();

        try {
            // Find current video in the courseVideos array
            const currentVideo = courseVideos.find(video => video.id === videoId);

            if (!currentVideo) {
                throw new Error("Video not found in course videos");
            }

            setVideoData(currentVideo);
            console.log("Video data loaded:", currentVideo);
            console.log("Watch history position:", currentVideo.current);

            // Find index of current video
            const currentIndex = courseVideos.findIndex(video => video.id === videoId);

            // Set up next video if available
            if (currentIndex < courseVideos.length - 1) {
                setUpNext(courseVideos[currentIndex + 1]);
            } else {
                setUpNext(null);
            }

            setLoading(false);
            setLoadingNextVideo(false);
        } catch (err) {
            console.error("Error setting video data:", err);
            setError("Failed to load video details");
            message.error("Failed to load video details");
            navigate(`/courses/${courseId}`);
        }
    }, [videoId, courseVideos, courseId, navigate]);

    // Handle beforeunload event
    useEffect(() => {
        const handleUnload = () => sendWatchTime();
        window.addEventListener("beforeunload", handleUnload);

        return () => {
            window.removeEventListener("beforeunload", handleUnload);
        };
    }, []);

    // Handle video navigation
    const handleVideoSelect = (video) => {
        clearNextVideoTimer();
        setVideoEnded(false);
        navigate(`/courses/${courseId}/videos/${video.id}`);
    };

    // Show loading state
    if (loading) {
        return (
            <div className="course-video-details loading">
                <div className="loading-container">
                    <Spin size="large" />
                    <p>Loading video...</p>
                </div>
            </div>
        );
    }

    // Show error state
    if (error || !videoData) {
        return (
            <div className="course-video-details error">
                <div className="error-container">
                    <Empty description={error || "Video not found"} />
                </div>
            </div>
        );
    }

    return (
        <div className="course-video-details">
            <style>{preventDownloadStyles}</style>
            <Row>
                <Col md={12}>
                    {/*<Heading back={true} heading={courseData?.title || "Course Video"} />*/}
                    <h3 className={'course-heading'}>
                        <span className={'back'} onClick={()=>{navigate(`/courses/${courseId}`)}} >
                                    <BackIcon />
                            </span>
                        {courseData?.title}
                    </h3>
                    <div className="course-video-content">
                        <div className="video-main-section">
                            {/* Video Player */}
                            <div
                                className="player"
                                onContextMenu={(e) => e.preventDefault()}
                            >
                                {videoEnded ? (
                                    <div className="video-ended-overlay">
                                        <div className="next-video-info">
                                            <div className="message">
                                                {upNext ? (
                                                    <div className="simple-next-message">
                                                        <div className="play-icon" onClick={handleNextVideo} style={{ cursor: 'pointer' }}>
                                                            <CoursesVideoIconBig/>
                                                        </div>
                                                        <div>
                                                            <h3>Up Next</h3>
                                                            <h4 className="video-title">{upNext.title}</h4>
                                                            <p className="countdown">Starts in <span className="countdown-number">{nextVideoCountdown}</span> second{nextVideoCountdown !== 1 ? 's' : ''}</p>

                                                        </div>
                                                    </div>
                                                ) : (
                                                    <p>This was the last video in the course</p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ) : loadingNextVideo ? (
                                    <div className="video-loading-overlay">
                                        <Spin size="large" />
                                        <p>Loading next video...</p>
                                    </div>
                                ) : null}

                                <ReactPlayer
                                    ref={playerRef}
                                    url={videoData.url}
                                    controls={true}
                                    height="100%"
                                    width="100%"
                                    light={videoData.thumbnail}
                                    playing={playing}
                                    onReady={handlePlayerReady}
                                    onProgress={handleProgress}
                                    onPause={handlePause}
                                    onEnded={handleVideoEnded}
                                    config={{
                                        file: {
                                            attributes: {
                                                controlsList: 'nodownload',
                                            },
                                        },
                                        youtube: {
                                            playerVars: {
                                                showinfo: 0,
                                                disablekb: 1,
                                                fs: 1,
                                                controls: 1,
                                                modestbranding: 1,
                                                rel: 0,
                                                download: 0
                                            }
                                        },
                                        vimeo: {
                                            playerOptions: {
                                                controls: true,
                                                pip: true,
                                                download: false
                                            }
                                        }
                                    }}
                                />
                            </div>

                            {/* Video navigation controls */}
                            <div className="video-navigation">
                                <button
                                    className="nav-button previous"
                                    onClick={handlePreviousVideo}
                                >
                                    <GoChevronLeft/> Previous
                                </button>

                                {/*<div className="video-info">*/}
                                {/*    <div className="video-number">Video #{videoData.number}</div>*/}
                                {/*    <div className="video-title">{videoData.title}</div>*/}
                                {/*</div>*/}

                                <button
                                    className="nav-button next"
                                    onClick={handleNextVideo}
                                >
                                    Next Video <GoChevronRight />
                                </button>
                            </div>

                            {/* Video description */}
                            <div className="video-description">
                                <div className="video-number">Video #{videoData.displayNumber || videoData.number}</div>
                                <h3>{videoData.title}</h3>
                                <p className="video-date">
                                    {videoData.date || dayjs(videoData.createdAt).format("DD-MM-YYYY")}
                                </p>
                                <p className="description-text">
                                    {videoData.description || "No description available for this video."}
                                </p>
                            </div>
                        </div>

                        {/* Sidebar with related videos */}
                        <div className="related-videos-section">
                            <h4 className="related-heading">Course Videos</h4>
                            <div className="related-videos-list">
                                {courseVideos.map((video) => (
                                    <div
                                        key={video.id}
                                        onClick={() => handleVideoSelect(video)}
                                        className={`related-video-item ${videoData.id === video.id ? 'active' : ''}`}
                                    >
                                        <CourseVideoCard
                                            {...video}
                                            number={video.displayNumber || video.number} // Make sure we display 1-based numbering
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Up Next section */}
                    {/* {upNext && !videoEnded && (
                        <div className="up-next-section">
                            <h4>Up Next</h4>
                            <div className="up-next-video" onClick={() => handleVideoSelect(upNext)}>
                                <CourseVideoCard 
                                    {...upNext} 
                                    number={upNext.displayNumber || upNext.number} // Make sure we display 1-based numbering
                                />
                            </div>
                        </div>
                    )} */}
                </Col>
            </Row>
        </div>
    );
};

export default CourseVideoDetails;
